import apiClient, { requestOrganizationContext } from '../utils/apiClient'

const ROLES = new Set(['OWNER', 'ADMIN', 'MEMBER'])
const KINDS = new Set(['PERSONAL', 'TEAM'])

function positiveId(value, field) {
	const normalized = String(value ?? '')
	if (!/^[1-9]\d*$/.test(normalized)) throw new TypeError(`Invalid ${field}`)
	return normalized
}

function normalizeMembership(value) {
	if (!value || value.status !== 'ACTIVE' || !ROLES.has(value.role)) {
		throw new TypeError('Organization membership must be ACTIVE with a supported role')
	}
	return Object.freeze({
		id: positiveId(value.id, 'membership.id'),
		role: value.role,
		status: 'ACTIVE',
		displayName: value.displayName ?? null,
		title: value.title ?? null,
		avatarObjectKey: value.avatarObjectKey ?? null,
	})
}

function normalizeOrganization(value) {
	if (!value || !KINDS.has(value.kind)) throw new TypeError('Invalid organization kind')
	if (value.kind === 'PERSONAL' && value.name !== null) throw new TypeError('PERSONAL organization name must be null')
	if (value.kind === 'TEAM' && (typeof value.name !== 'string' || !value.name.trim())) {
		throw new TypeError('TEAM organization name is required')
	}
	return Object.freeze({
		id: positiveId(value.id, 'organization.id'),
		kind: value.kind,
		reldoId: value.reldoId,
		name: value.name,
		systemLabelKey: value.kind === 'PERSONAL' ? value.systemLabelKey : null,
		logoObjectKey: value.logoObjectKey ?? null,
		membership: value.membership ? normalizeMembership(value.membership) : undefined,
		policies: value.policies ?? null,
	})
}

function normalizeInvitation(value) {
	if (!value || value.state !== 'PENDING' || !['ADMIN', 'MEMBER'].includes(value.role)) {
		throw new TypeError('Invalid pending invitation')
	}
	return Object.freeze({
		id: positiveId(value.id, 'invitation.id'),
		organizationId: positiveId(value.organizationId, 'invitation.organizationId'),
		email: String(value.email || ''),
		role: value.role,
		state: 'PENDING',
		expiresAt: value.expiresAt,
	})
}

function normalizeIncomingInvitation(value) {
	const invitation = normalizeInvitation(value)
	return Object.freeze({ ...invitation, organizationName: String(value.organizationName || '') })
}

export class OrganizationApi {
	constructor(http = apiClient, contextRequest = requestOrganizationContext) {
		this.http = http
		this.contextRequest = contextRequest
	}

	async list() {
		const response = await this.http.get('/api/organizations')
		if (!Array.isArray(response.data?.organizations)) throw new TypeError('Invalid organizations response')
		return response.data.organizations.map(normalizeOrganization)
	}

	async context(organizationId, generation) {
		const expectedId = positiveId(organizationId, 'selected organization id')
		const response = await this.contextRequest(expectedId, generation)
		const organization = normalizeOrganization(response.data?.organization)
		const membership = normalizeMembership(response.data?.membership)
		if (organization.id !== expectedId) throw new TypeError('Organization context does not match selection')
		return Object.freeze({ organization, membership, policies: response.data?.policies ?? null })
	}

	async createTeam(name) {
		const response = await this.http.post('/api/organizations/teams', { name })
		return Object.freeze({
			organization: normalizeOrganization({
				...response.data?.organization,
				membership: response.data?.membership,
			}),
			membership: normalizeMembership(response.data?.membership),
		})
	}

	async createInvitation(email, role) {
		const response = await this.http.post('/api/organization-invitations', { email, role })
		return Object.freeze({
			invitation: normalizeInvitation(response.data?.invitation),
			token: String(response.data?.token || ''),
		})
	}

	async listIncomingInvitations() {
		const response = await this.http.get('/api/organization-invitations/incoming')
		if (!Array.isArray(response.data?.invitations)) throw new TypeError('Invalid incoming invitations response')
		return response.data.invitations.map(normalizeIncomingInvitation)
	}

	async acceptInvitation(token) {
		const normalized = String(token || '').trim()
		if (!normalized) throw new TypeError('Invitation token is required')
		const response = await this.http.post(
			`/api/organization-invitations/${encodeURIComponent(normalized)}/accept`
		)
		return Object.freeze({
			organizationId: positiveId(response.data?.organizationId, 'accepted organization id'),
			membership: normalizeMembership(response.data?.membership),
		})
	}
}

export default new OrganizationApi()
